import { ReactNode, useState, useEffect, useCallback } from "react";
import { UserRole } from "../../__generated__/graphql";
import useCurrentUserOrganizations from "../../hooks/useCurrentUserOrganizations";
import { useAuth } from "../../hooks/useAuth";
import useUserInfos from "../../hooks/useUserInfos";
import { PartialOrganization } from "./types";
import { UserContext } from "./context";
import * as Sentry from "@sentry/react";

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const { userId, isAuthLoading } = useAuth();
  const [currentOrganization, setCurrentOrganizationState] =
    useState<PartialOrganization | null>(null);

  const {
    data,
    loading,
    error,
    isPartOfOrganization: hasOrganization,
  } = useCurrentUserOrganizations();
  const {
    data: userInfosData,
    loading: userInfosLoading,
    error: userInfosError,
    refetch: refetchUserInfos,
  } = useUserInfos();

  const [organizations, setOrganizations] = useState<
    PartialOrganization[] | null
  >(null);
  const [isPartOfOrganization, setIsPartOfOrganization] = useState<
    boolean | null
  >(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isUserContextLoading, setIsUserContextLoading] = useState(false);

  // Wrapper function that updates state and localStorage
  const setCurrentOrganization = useCallback(
    (organization: PartialOrganization | null) => {
      setCurrentOrganizationState(organization);
      if (organization) {
        localStorage.setItem("organizationId", organization.id);
      } else {
        localStorage.removeItem("organizationId");
      }
    },
    []
  );

  useEffect(() => {
    if (data?.organizations) {
      // Map organizations to PartialOrganization format
      const mappedOrganizations = data.organizations
        .map((organization) => {
          if (!organization) return null;

          return {
            id: organization.id,
            name: organization.name,
            organizationType: organization.organizationType,
          };
        })
        .filter((org): org is PartialOrganization => org !== null);

      setOrganizations(mappedOrganizations);

      // Set current organization based on stored ID or default logic
      const storedOrganizationId = localStorage.getItem("organizationId");

      if (storedOrganizationId) {
        const foundOrganization = mappedOrganizations.find(
          (organization) => organization.id === storedOrganizationId
        );
        if (foundOrganization) {
          setCurrentOrganization(foundOrganization);
          return; // Exit early if found
        }
      }

      // If no stored organization found, use default logic
      if (mappedOrganizations.length === 1) {
        // If the user has only one organization, select it by default
        setCurrentOrganization(mappedOrganizations[0]);
      } else {
        // Multiple organizations - don't auto-select, let user choose
        setCurrentOrganization(null);
      }
    }
  }, [data?.organizations, setCurrentOrganization]);

  useEffect(() => {
    if (hasOrganization) {
      setIsPartOfOrganization(true);
    } else if (hasOrganization === false) {
      setIsPartOfOrganization(false);
    }

    if (currentOrganization) {
      const foundOrganization = data?.organizations.find(
        (organization) => organization?.id === currentOrganization.id
      );

      if (foundOrganization?.users?.find((user) => user?.userId === userId)) {
        setUserRole(
          foundOrganization?.users?.find((user) => user?.userId === userId)
            ?.role ?? null
        );
      }
    }

    if (loading || isAuthLoading || userInfosLoading) {
      setIsUserContextLoading(true);
    } else {
      setIsUserContextLoading(false);
    }

    // Fix the fact that after a user creation he/she can't see some informations as it was not refetched
    refetchUserInfos();
  }, [
    userId,
    loading,
    userInfosLoading,
    refetchUserInfos,
    currentOrganization,
    hasOrganization,
    isAuthLoading,
    data?.organizations,
  ]);

  // Set Sentry user context when user information is available
  useEffect(() => {
    if (!import.meta.env.PROD) {
      return; // Only set user context in production
    }

    const userInfos = userInfosData?.user;

    if (userInfos && userId) {
      // Set user context with ID and email for easier issue identification
      Sentry.setUser({
        id: userInfos.id,
        email: userInfos.email,
      });
    } else if (!userId) {
      // Clear user context when user logs out
      Sentry.setUser(null);
    }
  }, [userInfosData?.user, userId]);

  // Compute userOrganization based on currentOrganization
  const userOrganization = currentOrganization
    ? data?.organizations
        ?.find((org) => org?.id === currentOrganization.id)
        ?.users.find((user) => user?.userId === userId) ?? null
    : null;

  return (
    <UserContext.Provider
      value={{
        organizations,
        currentOrganization,
        userRole,
        isPartOfOrganization,
        isUserContextLoading,
        setCurrentOrganization,
        error: error ?? userInfosError ?? null,
        userOrganization,
        userInfos: userInfosData?.user ?? null,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
